import { apiRefusalReason } from './errors';

/**
 * Joining a client's Sentry event to the API's side of the same request —
 * shared by web and mobile so both file the same tags.
 *
 * The API (flashka-web/server/observability/sentry.ts) continues the caller's
 * `sentry-trace`, so its error events and log lines carry the caller's trace
 * id, and it echoes that id as `x-sentry-trace-id` on every answer. Vercel
 * answers its own request id as `x-vercel-id`.
 */
export const API_TRACE_ID_HEADER = 'x-sentry-trace-id';
export const VERCEL_REQUEST_ID_HEADER = 'x-vercel-id';

/** What our API said about a failed answer beyond its status. */
export type ApiFailureDetails = {
  /** The refusal's stable code (`ApiRefusalBody.reason`). */
  reason?: string;
  /** The trace the API's events and log lines for this request carry. */
  traceId?: string;
  /** Vercel's request id: the key to the Vercel log line. */
  requestId?: string;
};

/**
 * A `fetch` `Headers`, an `AxiosHeaders` (both have `get`), or the plain
 * lower-cased object axios hands over in some environments.
 */
export type HeaderSource =
  | { get(name: string): unknown }
  | Record<string, unknown>
  | null
  | undefined;

function header(headers: HeaderSource, name: string): string | undefined {
  if (!headers) return undefined;
  const value =
    typeof (headers as { get?: unknown }).get === 'function'
      ? (headers as { get(name: string): unknown }).get(name)
      : (headers as Record<string, unknown>)[name];
  return typeof value === 'string' && value ? value : undefined;
}

/** The reason code and the server-side ids of a failed answer. */
export function apiFailureDetails(
  headers: HeaderSource,
  body: unknown,
): ApiFailureDetails {
  return {
    reason: apiRefusalReason(body),
    traceId: header(headers, API_TRACE_ID_HEADER),
    requestId: header(headers, VERCEL_REQUEST_ID_HEADER),
  };
}

/**
 * Tags and context for the Sentry event of a failed API answer: `api.reason`
 * says which refusal it was, `api.trace_id` leads to the server's side (the
 * caller's own trace when the request propagated it, searchable either way).
 * The caller adds its own status tag, under the name its platform already uses.
 */
export function apiFailureReportContext(
  status: number | undefined,
  details: ApiFailureDetails,
): {
  tags: Record<string, string>;
  contexts: { api: { status?: number } & ApiFailureDetails };
} {
  const { reason, traceId, requestId } = details;
  return {
    tags: {
      ...(reason ? { 'api.reason': reason } : {}),
      ...(traceId ? { 'api.trace_id': traceId } : {}),
    },
    contexts: { api: { status, reason, traceId, requestId } },
  };
}

/**
 * Where a client sends `sentry-trace` / `baggage`: our own `/api`, nothing
 * else. On the web the API is same-origin, and the browser SDK tests a path
 * pattern against the path of same-origin requests only. A native client
 * calls absolute URLs, so each API origin it may use is matched in full —
 * React Native's default (match every URL) would hand our baggage to every host the
 * app talks to, presigned storage uploads included.
 */
export function apiTracePropagationTargets(
  apiOrigins: readonly (string | null | undefined)[] = [],
): RegExp[] {
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const origins = [
    ...new Set(
      apiOrigins
        .filter((origin): origin is string => !!origin)
        .map((origin) => origin.replace(/\/+$/, '')),
    ),
  ];
  return [/^\/api\//, ...origins.map((origin) => new RegExp(`^${escape(origin)}/api/`))];
}
